import { useEffect, useState } from "react";
import { useSeo } from "@/lib/seo";
import { getRegistryFor, DEFAULT_ENGINE } from "@/themes/registry";
import { resolveAnchors } from "@/lib/sectionAnchor";
import { isStudioPreview } from "@/lib/studioPreviewBridge";
import { fetchTemplateSections, peekTemplateSections } from "@/lib/themeSections";
import RippleLoader from "@/components/RippleLoader";

const { DEFAULT_SECTION_ORDER } = getRegistryFor(DEFAULT_ENGINE);
// Repli utilisé UNIQUEMENT en cas d'échec de l'API pour garder la page
// lisible. Ne sert plus d'état initial — null est l'état de chargement,
// ce qui évite le flash des sections agence par défaut avant que les
// vraies sections de la boutique soient connues.
const DEFAULT_SECTIONS = DEFAULT_SECTION_ORDER.map((type, order) => ({ id: type, type, enabled: true, order }));

// Cache et déduplication vivent dans lib/themeSections.js, partagés avec
// useTemplateSections.js et TemplateSections.jsx.

export default function Home() {
  useSeo("home");
  // Cache hit → rendu immédiat sans attente réseau.
  const cached = peekTemplateSections("home");
  const [sections, setSections] = useState(cached ? (cached.sections || DEFAULT_SECTIONS) : null);
  const [engine, setEngine] = useState(cached ? cached.engine : DEFAULT_ENGINE);

  useEffect(() => {
    let active = true;
    fetchTemplateSections("home").then((result) => {
      if (!active) return;
      // `sections` nul = API indisponible, tableau vide = thème sans section
      // configurée : dans les deux cas on affiche le repli agence pour que la
      // page reste lisible plutôt que vide.
      setSections(result.sections && result.sections.length ? result.sections : DEFAULT_SECTIONS);
      setEngine(result.engine);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    // Dans le Canvas du Studio de thème (iframe ?studio_preview=1), le
    // brouillon non enregistré prévaut sur ce que l'API a renvoyé — voir
    // lib/studioPreviewBridge.js. `template` (Sprint 11) précise à quel
    // domaine appartient ce brouillon : Home.jsx n'applique que "home",
    // jamais un brouillon d'En-tête/Pied de page en cours d'édition.
    if (!isStudioPreview()) return undefined;
    const onDraft = (e) => {
      if ((e.detail?.template || "home") !== "home") return;
      setSections(e.detail.sections);
    };
    window.addEventListener("studio:sections", onDraft);
    return () => window.removeEventListener("studio:sections", onDraft);
  }, []);

  const { SECTION_REGISTRY } = getRegistryFor(engine);
  if (!sections) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <RippleLoader size={40} />
    </div>
  );
  const active = sections
    .filter((s) => s.enabled && SECTION_REGISTRY[s.type || s.key])
    .sort((a, b) => a.order - b.order);
  // Ancre #… de chaque section (voir lib/sectionAnchor.js) : posée sur le
  // conteneur quand le composant n'en porte pas lui-même, pour que tout
  // bloc soit atteignable par un lien de bouton ou de menu.
  const anchors = resolveAnchors(sections, engine);

  return (
    <div className="overflow-hidden animate-fade-in">
      {active.map((s) => {
        const type = s.type || s.key;
        const id = s.id || type;
        const { Component } = SECTION_REGISTRY[type];
        const anchor = anchors[id];
        return (
          <div key={id} data-section-key={id} data-section-type={type} id={anchor?.own ? undefined : anchor?.anchor} className={anchor?.own ? undefined : "scroll-mt-28"}>
            <Component props={s.props} blocks={s.blocks} sectionId={id} />
          </div>
        );
      })}
    </div>
  );
}
